import {
  BarChart2,
  CalendarClock,
  FileText,
  HandCoins,
  Moon,
  Sun,
  Users,
  Settings,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect, memo } from "react";
import api, { API_URL } from "../../services/api";
const navItems = [
  {
    name: "Configuration",
    icon: <FileText className="w-5 h-5" />,
    module: "configuration",
  },
  { name: "Employee", icon: <Users className="w-5 h-5" />, module: "employee" },
  {
    name: "Attendance",
    icon: <CalendarClock className="w-5 h-5" />,
    module: "attendance",
  },
  {
    name: "Payroll",
    icon: <HandCoins className="w-5 h-5" />,
    module: "payroll",
  },
  // { name: "Reports", icon: <BarChart2 className="w-5 h-5" /> },
];

const Sidebar = memo(
  function Sidebar({ setCurrentPage, darkMode, toggleDarkMode }) {
    const navigate = useNavigate();
    const [companyData, setCompanyData] = useState(null);

    useEffect(() => {
      const storedData = JSON.parse(localStorage.getItem("companyData"));
      setCompanyData(storedData);
      console.log("[DEBUG Sidebar] Data read from localStorage:", storedData);

      // 🔁 Always refresh latest data from backend using axios instance
      if (storedData?.id) {
        api
          .get(`/companies/${storedData.id}`)
          .then((response) => {
            const data = response.data;
            if (data.success && data.company) {
              console.log(
                "[DEBUG Sidebar] Updated company data from API:",
                data.company
              );
              setCompanyData(data.company);
              localStorage.setItem("companyData", JSON.stringify(data.company));
            } else {
              console.warn("Sidebar API response invalid:", data);
            }
          })
          .catch((err) =>
            console.error("Failed to refresh company data:", err)
          );
      }

      // 🆕 Listen for company data updates (from SuperAdmin)
      const handleCompanyUpdate = (e) => {
        console.log("[Sidebar] 🔄 Company updated via event:", e.detail);
        setCompanyData(e.detail);
      };

      window.addEventListener("companyDataUpdated", handleCompanyUpdate);
      return () =>
        window.removeEventListener("companyDataUpdated", handleCompanyUpdate);
    }, []);

    const handleLogoClick = () => {
      navigate("/admin/home");
      setCurrentPage("Home");
    };

    const handleEmployeeTabClick = () => {
      // Check if we're already on the employee page and emit event to close any open form
      if (window.location.pathname === "/admin/employee") {
        // Dispatch custom event to close employee form
        window.dispatchEvent(new CustomEvent("closeEmployeeForm"));
      }
      setCurrentPage("Employee");
    };
    const getModuleStatus = (moduleName) => {
      if (!companyData) {
        // Default to enabled while data is loading
        return { isEnabled: true };
      }

      let isEnabled = true;
      switch (moduleName) {
        case "employee":
          // Checks if module_employee is 0 (off).
          // If it's undefined (for older companies) or 1 (on), it remains enabled.
          isEnabled = Number(companyData.module_employee) !== 0;

          break;
        case "attendance":
          isEnabled = companyData.module_attendance !== 0;
          break;
        case "payroll":
          isEnabled = companyData.module_payroll !== 0;
          break;
        default:
          isEnabled = true; // 'Configuration' and 'Home' are always enabled
      }
      return { isEnabled };
    };
    const getLinkClass = (moduleName) => {
      const { isEnabled } = getModuleStatus(moduleName);

      if (isEnabled) {
        // Your original styles for an active, clickable link
        return "flex items-center space-x-4 px-4 py-3.5 text-gray-700 hover:text-gray-900 hover:bg-white rounded-xl transition-all duration-200 group";
      } else {
        // New styles for a VISIBLE BUT DISABLED link
        return "flex items-center space-x-4 px-4 py-3.5 text-gray-400 rounded-xl transition-all duration-200 group opacity-60 cursor-not-allowed";
      }
    };
    const handleLinkClick = (event, item) => {
      const moduleName = item.module; // Use the new module key
      const { isEnabled } = getModuleStatus(moduleName);

      if (!isEnabled) {
        event.preventDefault(); // This stops navigation
        alert(`The ${item.name} module is not enabled for your company plan.`);
      } else if (item.name === "Employee") {
        handleEmployeeTabClick(); // Run your specific employee logic
      } else {
        setCurrentPage(item.name);
      }
    };

    return (
      <aside className="w-44 h-screen bg-[#F5F5F5] shadow-lg border-r border-gray-200">
        <div className="flex flex-col h-full">
          {/* Company Logo Section */}
          <div className="h-20 border-b border-gray-200 flex items-center justify-center">
            <div
              onClick={handleLogoClick}
              className="flex flex-col items-center cursor-pointer hover:opacity-80 transition-opacity duration-200"
            >
              {companyData?.logo ? (
                <img
                  src={companyData.logo}
                  alt={`${companyData.name} logo`}
                  className="w-28 h-20 object-contain"
                  style={{ imageRendering: "high-quality" }}
                  onError={(e) => {
                    // If logo fails to load, hide the img and show SVG
                    e.target.style.display = "none";
                    e.target.nextSibling.style.display = "block";
                  }}
                />
              ) : null}
              <svg
                className={`w-24 h-24 ${
                  companyData?.logo ? "hidden" : ""
                } text-gray-600`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
          </div>

          {/* Navigation Items */}
          <nav className="flex-1 px-4 py-4">
            <div className="space-y-1">
              {navItems
                // ✅ Filter out modules that are OFF
                .filter((item) => {
                  if (!companyData) return true; // show all while loading
                  switch (item.module) {
                    case "employee":
                      return Number(companyData.module_employee) !== 0;
                    case "attendance":
                      return Number(companyData.module_attendance) !== 0;
                    case "payroll":
                      return Number(companyData.module_payroll) !== 0;
                    default:
                      return true; // configuration and others always visible
                  }
                })
                .map((item, index) => (
                  <Link
                    key={index}
                    to={`/admin/${
                      item.name === "Configuration"
                        ? "masters"
                        : item.name
                            .toLowerCase()
                            .replace(/ & /g, "-")
                            .replace(/ /g, "-")
                    }`}
                    onClick={
                      item.name === "Employee"
                        ? handleEmployeeTabClick
                        : () => setCurrentPage(item.name)
                    }
                    className="flex items-center space-x-4 px-4 py-3.5 text-gray-700 hover:text-gray-900 hover:bg-white rounded-xl transition-all duration-200 group"
                  >
                    <span className="text-gray-600 group-hover:text-gray-700 transition-colors duration-200">
                      {item.icon}
                    </span>
                    <span className="font-medium text-sm">{item.name}</span>
                  </Link>
                ))}
            </div>
          </nav>

          {/* Refresh button */}
          <div className="p-4">
            <button
              onClick={() => window.location.reload()}
              className="text-xs text-blue-500 underline"
            >
              Refresh Company Data
            </button>
          </div>

          {/* Global Settings */}
          {/* <div className="p-4 border-t border-gray-200">
            <Link
              to="/admin/global-settings"
              onClick={() => setCurrentPage("Global Settings")}
              className="flex items-center space-x-4 w-full px-4 py-3.5 text-gray-700 hover:text-gray-900 hover:bg-white rounded-xl transition-all duration-200 group"
            >
              <span className="text-gray-600 group-hover:text-gray-700 transition-colors duration-200">
                <Settings className="w-5 h-5" />
              </span>
              <span className="font-medium text-sm">Global Settings</span>
            </Link>
          </div> */}

          {/* Dark Mode Toggle */}
          {/* <div className="p-4 border-t border-gray-100">
          <button
            onClick={toggleDarkMode}
            className="flex items-center space-x-4 w-full px-4 py-3.5 text-gray-700 hover:text-gray-900 hover:bg-white rounded-xl transition-all duration-200 group"
          >
            <span className="text-gray-600 group-hover:text-gray-700 transition-colors duration-200">
              {darkMode ? <Sun size={20} /> : <Moon size={20} />}
            </span>
            <span className="font-medium text-sm">
              {darkMode ? 'Light Mode' : 'Dark Mode'}
            </span>
          </button>
        </div> */}
        </div>
      </aside>
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if darkMode changes
    return prevProps.darkMode === nextProps.darkMode;
  }
);

export default Sidebar;
